import React, { FC, useRef, useEffect } from 'react'
import { useId } from '@reach/auto-id'
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import { XYChart } from '@amcharts/amcharts4/charts'
import am4themes_animated from '@amcharts/amcharts4/themes/animated'

am4core.useTheme(am4themes_animated)

export type TrafficSeries = {
  bars: number[]
  line: number[]
  labels: string[]
}

type TrafficChartProps = {
  data: TrafficSeries
}

const transformData = (data: TrafficSeries): any[] => {
  return data.labels.map((label, index) => ({
    label,
    bar: data.bars[index] ?? 0,
    line: data.line[index] ?? 0,
    zero: 0,
  }))
}

export const TrafficChart: FC<TrafficChartProps> = ({ data }) => {
  const chartId = useId()
  const chartRef = useRef<XYChart>()
  const transformedData = transformData(data)
  const maxDemand = Math.max(100, ...data.line)

  // -- Setup chart
  useEffect(() => {
    const chart = am4core.create(chartId, am4charts.XYChart)
    chart.data = transformedData
    chart.zoomOutButton.disabled = true

    const SpeedAxis = chart.xAxes.push(new am4charts.CategoryAxis())
    SpeedAxis.dataFields.category = 'label'
    SpeedAxis.renderer.cellStartLocation = 0
    SpeedAxis.renderer.cellEndLocation = 1
    // SpeedAxis.renderer.grid.template.opacity = 0
    SpeedAxis.renderer.labels.template.maxWidth = 150
    SpeedAxis.renderer.labels.template.truncate = true
    SpeedAxis.renderer.minGridDistance = 50
    SpeedAxis.renderer.minLabelPosition = 0
    SpeedAxis.renderer.marginLeft = -20
    SpeedAxis.renderer.fontSize = 12
    SpeedAxis.renderer.grid.template.opacity = 0
    SpeedAxis.renderer.fullWidthTooltip = true

    const speedValueAxis = chart.yAxes.push(new am4charts.ValueAxis())
    speedValueAxis.min = 0
    speedValueAxis.renderer.line.stroke = am4core.color('#cccccc')
    speedValueAxis.renderer.line.strokeOpacity = 1
    speedValueAxis.renderer.fontSize = 12
    speedValueAxis.renderer.grid.template.opacity = 0
    speedValueAxis.renderer.axis.title.text = 'Speed (Km/h)'
    speedValueAxis.cursorTooltipEnabled = false

    const demandValueAxis = chart.yAxes.push(new am4charts.ValueAxis())
    demandValueAxis.min = 0
    demandValueAxis.max = maxDemand
    demandValueAxis.renderer.opposite = true
    demandValueAxis.renderer.line.stroke = am4core.color('#cccccc')
    demandValueAxis.renderer.line.strokeOpacity = 1
    demandValueAxis.renderer.fontSize = 12
    demandValueAxis.renderer.axis.title.text = 'Demand (%)'
    demandValueAxis.cursorTooltipEnabled = false

    // -- Demand series
    const demandSeries = chart.series.push(new am4charts.LineSeries())
    // Binding data to the series
    demandSeries.dataFields.categoryX = 'label'
    demandSeries.dataFields.valueY = 'line'
    demandSeries.yAxis = demandValueAxis

    // Animation
    demandSeries.interpolationDuration = 500
    demandSeries.sequencedInterpolation = true

    // Visuals
    demandSeries.height = am4core.percent(100)
    demandSeries.strokeWidth = 1
    demandSeries.fillOpacity = 0.3
    demandSeries.fill = am4core.color('#394991')
    demandSeries.stroke = am4core.color('#394991')
    demandSeries.zIndex = 1

    // Tooltip
    demandSeries.tooltipText = "{valueY.formatNumber('#.00')}%"

    if (demandSeries.tooltip) {
      demandSeries.tooltip.getFillFromObject = false
      demandSeries.tooltip.getStrokeFromObject = false
      demandSeries.tooltip.autoTextColor = false
      demandSeries.tooltip.background.fill = am4core.color('#fff')
      demandSeries.tooltip.background.fillOpacity = 1
      demandSeries.tooltip.label.fill = am4core.color('#777')
    }

    // Points the the line
    const bullet = demandSeries.bullets.push(new am4charts.Bullet())
    bullet.fill = am4core.color('#fff')

    const circle = bullet.createChild(am4core.Circle)
    circle.width = 8
    circle.height = 8
    circle.fill = am4core.color('#394991')
    circle.stroke = am4core.color('#394991')

    // -- Speed series
    const speedSeries = chart.series.push(new am4charts.ColumnSeries())
    // Binding data to the series
    speedSeries.dataFields.categoryX = 'label'
    speedSeries.dataFields.valueY = 'bar'

    // Animation
    speedSeries.interpolationDuration = 500
    speedSeries.sequencedInterpolation = true

    speedSeries.columns.template.height = am4core.percent(100)
    speedSeries.columns.template.strokeWidth = 0
    speedSeries.columns.template.fill = am4core.color('#7899fb')

    // Tooltip
    speedSeries.columns.template.tooltipText = '{valueY} km/h'
    speedSeries.columns.template.tooltipY = 0

    if (speedSeries.tooltip) {
      speedSeries.tooltip.getFillFromObject = false
      speedSeries.tooltip.pointerOrientation = 'vertical'
      speedSeries.tooltip.dy = -5
      speedSeries.tooltip.background.fillOpacity = 1
      speedSeries.tooltip.background.fill = am4core.color('#FFF')
      speedSeries.tooltip.label.fill = am4core.color('#777')
    }

    chart.cursor = new am4charts.XYCursor()
    chart.cursor.behavior = 'panX'

    chartRef.current = chart

    // Remove ugly amCharts icon xD (
    chart.events.on('validated', () => {
      chart.logo.height = -15
      //document.querySelector(`g[opacity^="0.4"`)?.remove()
    })

    return () => {
      chartRef.current?.dispose()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartId, maxDemand, `lenght-${data.labels.length}`])

  // -- Update data
  useEffect(() => {
    if (!chartRef.current) return

    transformedData.forEach((_, index) => {
      const item = chartRef.current!.data[index]
      Object.keys(item).forEach((key) => {
        item[key] = transformedData[index][key]
      })
    })

    chartRef.current.invalidateRawData()
  }, [transformedData])

  return <div id={chartId} style={{ width: '100%', height: '100%' }}></div>
}
